import { Box, Typography } from '@material-ui/core';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resendConfirmationEmailThunk, resetPasswordEmailThunk } from '@redux/oauth/oauthThunks';
import { ResetPasswordEmailPayload } from '@redux/oauth/oauthTypes';
import { getAcceptedInvitationByEmailThunk } from '@redux/person/personThunks';
import { AppDispatch } from '@redux/store';
import BackWithTitle from 'components/BackWithTitle/BackWithTitle';
import { RouteNames } from 'utils/navigationUtils';
import { getQueryParamObject } from 'utils/util';
import style from './ForgotPasswordEmailPane.scss';

interface Props {
  loadingState: [boolean, Dispatch<SetStateAction<boolean>>];
  handleExternalRouting: (url?: string) => void;
  handleError: (err: any) => void;
}

export const ForgotPasswordEmailPane: React.FC<Props> = ({ loadingState, handleExternalRouting, handleError }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [email, setEmail] = useState<string>('');
  const [processed, setProcessed] = useState<boolean>(false);

  useEffect(() => {
    // Determine email from navigation state or query parameter
    const stateEmail = (location.state as any)?.email;
    const queryEmail = getQueryParamObject(location.search)?.email as string | undefined;
    const resolvedEmail = stateEmail || queryEmail || '';
    setEmail(resolvedEmail);
  }, [location]);

  useEffect(() => {
    if (!email || processed) {
      return;
    }
    setProcessed(true);
    loadingState[1](true);
    (async () => {
      let accepted = false;
      try {
        // Determine if invitation has been accepted
        accepted = await dispatch(getAcceptedInvitationByEmailThunk(email));
      } catch (error) {
        loadingState[1](false);
        return;
      }
      try {
        if (!accepted) {
          await dispatch(resendConfirmationEmailThunk(email));
        } else {
          await dispatch(resetPasswordEmailThunk({ email } as ResetPasswordEmailPayload));
        }
      } catch (error) {
        handleError(error);
      } finally {
        loadingState[1](false);
      }
    })();
  }, [email, loadingState, processed, dispatch, handleError]);

  return (
    <Box className={style.inputBox}>
      <Box className={style.backButton}>
        <BackWithTitle title="Back to login" onClick={() => navigate(RouteNames.LOGIN)} />
      </Box>
      <Typography className={style.mainHeader}>A confirmation email has been sent to {email}.</Typography>
      <Typography className={style.instructionHeader}>
        If the email contains a validation code,&nbsp;
        <span style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(RouteNames.RESET_PASSWORD, { state: { email }, replace: true })}>
          click here
        </span>
        &nbsp;to enter the code and reset your password.
      </Typography>
    </Box>
  );
};
